import EventVideoProcessing from '../../components/events/EventVideoProcessing';
import EventMediaModal from '../../components/events/EventMediaModal';
import EventPlaybackModal from '../../components/events/EventPlaybackModal';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import EventFaceMatches from '../../components/events/EventFaceMatches';
import { ArrowLeft, Expand, Edit2, Check, X, Video, Image as ImageIcon, Play } from 'lucide-react';
import { eventService, integrationService } from '../../services';
import { useLocation } from '../../contexts/LocationContext';
import { useEventPresignedUrl } from '../../hooks';
import type { Event } from '../../types';
import { formatEventDate } from '../../utils/eventTime';

const ViewEventPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { locations } = useLocation();
  const [event, setEvent] = useState<Event | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [showPlaybackModal, setShowPlaybackModal] = useState(false);
  const [recording, setRecording] = useState<{ eventId: number; url: string | null } | null>(null);
  const s3VideoUrl = recording?.eventId === event?.event_id ? recording?.url : null;
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerType, setViewerType] = useState<'image' | 'video'>('image');
  const [checkingS3Video, setCheckingS3Video] = useState(false);
  const currentId = useRef(id); currentId.current = id;
  const mounted = useRef(true);

  // Get presigned URLs for media
  const snapshotDirectUrl = event?.s3_url || event?.event_snapshot_url || event?.event_thumbnail_url;
  const { presignedUrl: snapshotPresignedUrl, isLoading: isLoadingSnapshot } = useEventPresignedUrl(
    event?.event_id,
    event?.s3_url || event?.event_snapshot_url ? 'snapshot' : 'thumbnail',
    snapshotDirectUrl
  );
  
  useEffect(() => {
    mounted.current = true;
    if (id) {
      fetchEvent();
    }
    return () => { mounted.current = false; };
  }, [id]);

  // Never reuse a recording URL or a late response from a different event.
  useEffect(() => {
    if (!event?.event_id) return;
    let active = true;
    const eventId = event.event_id;
    setRecording({ eventId, url: null });
    setCheckingS3Video(true);
    integrationService.getEventVideoUrl(eventId).then(result => {
      if (active) setRecording({ eventId, url: result.success && result.has_video ? result.video_url || null : null });
    }).catch(() => {
      if (active) setRecording({ eventId, url: null });
    }).finally(() => { if (active) setCheckingS3Video(false); });
    return () => { active = false; };
  }, [event?.event_id, event?.event_video_url]);

  const fetchEvent = async (showLoading = true) => {
    const requestedId = id;
    try {
      if (showLoading) { setIsLoading(true); setError(null); }
      const data = await eventService.getEventById(parseInt(id!));
      if (mounted.current && currentId.current === requestedId) setEvent(data);
    } catch (err) {
      if (showLoading && mounted.current && currentId.current === requestedId) setError(err instanceof Error ? err.message : 'Failed to fetch event');
    } finally {
      if (showLoading && mounted.current && currentId.current === requestedId) setIsLoading(false);
    }
  };

  const openViewer = (type: 'image' | 'video') => { setViewerType(type); setViewerOpen(true); };

  const handleAcknowledge = async () => {
    if (!event) return;
    
    if (!window.confirm('Are you sure you want to acknowledge this event?')) {
      return;
    }
    
    try {
      setIsAcknowledging(true);
      const updated = await eventService.acknowledgeEvent(event.event_id);
      setEvent(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge event');
    } finally {
      setIsAcknowledging(false);
    }
  };

  if (isLoading) {
    return (
      <MainLayout>
        <Loading size="lg" text="Loading event..." />
      </MainLayout>
    );
  }

  if (error || !event) {
    return (
      <MainLayout>
        <Alert variant="error" title="Error" message={error || 'Event not found'} />
      </MainLayout>
    );
  }

  const formatDate = (date?: string, locationId?: number, isEventTime = false) => {
    const timeZone = event.location_timezone || locations.find(location => location.ycl_id === locationId)?.location_timezone;
    const text = formatEventDate(date, { timeZone, includeSeconds: true,
      naiveIsLocal: isEventTime && event.event_source === 'cloud_alarm' });
    return date && timeZone ? `${text} (${timeZone})` : text;
  };

  const getStateVariant = (state?: string): 'success' | 'warning' | 'danger' | 'info' => {
    switch (state?.toLowerCase()) {
      case 'active': return 'warning';
      case 'resolved': return 'success';
      case 'dismissed': return 'info';
      default: return 'info';
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              leftIcon={<ArrowLeft size={18} />}
              onClick={() => navigate('/events')}
            >
              Back to Events
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-text-primary">
                Event #{event.event_id}
              </h1>
              <p className="text-sm text-text-muted mt-1">
                {event.event_code && `Code: ${event.event_code}`}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {!event.acknowledged && (
              <Button
                variant="success"
                leftIcon={<Check size={18} />}
                onClick={handleAcknowledge}
                isLoading={isAcknowledging}
              >
                Acknowledge
              </Button>
            )}
            <Button
              variant="primary"
              leftIcon={<Edit2 size={18} />}
              onClick={() => navigate(`/events/${event.event_id}/edit`)}
            >
              Edit Event
            </Button>
          </div>
        </div>

        {/* Event Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Information */}
          <Card className="lg:col-span-2">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Event Information</h2>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-text-muted">Event Type</label>
                <p className="text-base text-text-primary mt-1">
                  {event.event_type || 'N/A'}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-text-muted">Event State</label>
                <div className="mt-1">
                  <Badge variant={getStateVariant(event.event_state)}>
                    {event.event_state || 'N/A'}
                  </Badge>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-text-muted">Camera</label>
                <p className="text-base text-text-primary mt-1">
                  {event.camera_name || event.event_camera || 'N/A'}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-text-muted">Location</label>
                <p className="text-base text-text-primary mt-1">
                  {event.location_name || (event.event_location ? `Location #${event.event_location}` : 'N/A')}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-text-muted">Event Tag</label>
                <p className="text-base text-text-primary mt-1">
                  {event.event_tag || 'N/A'}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-text-muted">Confidence</label>
                <p className="text-base text-text-primary mt-1">
                  {event.confidence != null ? `${(event.confidence * 100).toFixed(1)}%` : 'N/A'}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-text-muted">MAC Address</label>
                <p className="text-base text-text-primary mt-1 font-mono text-sm">
                  {event.event_mac || 'N/A'}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-text-muted">Event Time</label>
                <p className="text-base text-text-primary mt-1">
                  {formatDate(event.created_date, event.event_location, true)}
                </p>
              </div>

              <div className="col-span-2">
                <label className="text-sm font-medium text-text-muted">Notes</label>
                <p className="text-base text-text-primary mt-1">
                  {event.event_note || 'No notes'}
                </p>
              </div>
            </div>
          </Card>

          {/* Status & Processing */}
          <Card>
            <h2 className="text-lg font-semibold text-text-primary mb-4">Status</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-text-muted">Acknowledged</label>
                <div className="flex items-center gap-2 mt-1">
                  {event.acknowledged ? (
                    <>
                      <Check size={18} className="text-status-success" />
                      <span className="text-sm text-status-success">Yes</span>
                    </>
                  ) : (
                    <>
                      <X size={18} className="text-text-muted" />
                      <span className="text-sm text-text-muted">No</span>
                    </>
                  )}
                </div>
                {event.acknowledged_at && (
                  <p className="text-xs text-text-muted mt-1">
                    {formatDate(event.acknowledged_at, event.event_location)}
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-text-muted">Processed</label>
                <div className="flex items-center gap-2 mt-1">
                  {event.processed ? (
                    <>
                      <Check size={18} className="text-status-success" />
                      <span className="text-sm text-status-success">Yes</span>
                    </>
                  ) : (
                    <>
                      <X size={18} className="text-text-muted" />
                      <span className="text-sm text-text-muted">No</span>
                    </>
                  )}
                </div>
                {event.processed_at && (
                  <p className="text-xs text-text-muted mt-1">
                    {formatDate(event.processed_at, event.event_location)}
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>

        <EventFaceMatches eventId={event.event_id} />

        {/* Media */}
        <EventVideoProcessing eventId={event.event_id} onReady={() => { void fetchEvent(false); }} />

        {(snapshotDirectUrl || event.event_video_url || event.event_camera || event.camera_id) && (
          <Card>
            <h2 className="text-lg font-semibold text-text-primary mb-4">Media</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {!snapshotDirectUrl && <div className="rounded-lg bg-surface-secondary p-6 flex flex-col items-center justify-center text-center min-h-48">
                <ImageIcon size={28} className="text-text-muted mb-2" />
                <p className="text-sm text-text-secondary">The event screenshot will appear here when available.</p>
                <p className="text-xs text-text-muted mt-1">You can continue viewing events while the video is prepared.</p>
              </div>}
              {snapshotDirectUrl && (
                <div>
                  <label className="text-sm font-medium text-text-muted mb-2 block flex items-center gap-2">
                    <ImageIcon size={16} />
                    Snapshot
                  </label>
                  {isLoadingSnapshot ? (
                    <div className="w-full h-48 rounded-lg border border-surface-secondary bg-surface-secondary flex items-center justify-center">
                      <div className="animate-spin h-8 w-8 border-2 border-primary-500 border-t-transparent rounded-full" />
                    </div>
                  ) : (
                    <button type="button" onClick={() => openViewer('image')} aria-label="Expand event snapshot"
                      className="group relative block w-full overflow-hidden rounded-xl border border-border-default bg-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-primary">
                      <img src={snapshotPresignedUrl} alt="Event snapshot" className="max-h-[28rem] w-full object-contain" />
                      <span className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-lg bg-black/70 px-3 py-2 text-xs text-white"><Expand size={15} />Expand snapshot</span>
                    </button>
                  )}
                </div>
              )}
              <div>
                <label className="text-sm font-medium text-text-muted mb-2 flex items-center gap-2"><Video size={16} />Event recording</label>
                <div className="flex min-h-[16rem] flex-col items-center justify-center gap-4 rounded-xl border border-border-default bg-surface-secondary p-6 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-primary/10 text-accent-primary"><Play size={26} /></span>
                  <div><p className="font-medium text-text-primary">{s3VideoUrl ? 'Saved recording ready' : 'Watch from your camera'}</p>
                    <p className="mt-2 max-w-sm text-sm text-text-muted">{s3VideoUrl ? 'View the saved event clip or open the camera’s recording.' : 'Open the recording around this event directly from your recorder or cloud storage.'}</p></div>
                  <div className="flex flex-wrap justify-center gap-3">
                    {s3VideoUrl && <Button leftIcon={<Play size={16} />} onClick={() => openViewer('video')}>Play event video</Button>}
                    {(event.event_camera || event.camera_id) && <Button variant={s3VideoUrl ? 'outline' : 'primary'} leftIcon={<Video size={16} />} onClick={() => setShowPlaybackModal(true)}>Watch camera playback</Button>}
                  </div>
                  <p className="text-xs text-text-muted">{checkingS3Video ? 'Checking for a saved clip. Camera playback is available now.' : 'Starts 1 minute before the event · Adjust the time window in the viewer'}</p>
                </div>
              </div>
            </div>
          </Card>
        )}

        {event.ai_description && (
          <Card>
            <h2 className="text-lg font-semibold text-text-primary mb-4">AI Analysis</h2>
            <p className="text-sm text-text-secondary whitespace-pre-line">{event.ai_description}</p>
          </Card>
        )}

      </div>

      <EventMediaModal event={viewerOpen ? event : null} mediaType={viewerType}
        onClose={() => setViewerOpen(false)} onMediaTypeChange={setViewerType} videoUrl={s3VideoUrl} />

      {showPlaybackModal && <EventPlaybackModal key={event.event_id} event={event}
        locationTimezone={locations.find(location => location.ycl_id === event.event_location)?.location_timezone}
        onClose={() => setShowPlaybackModal(false)}
        onSnapshot={snapshotDirectUrl ? () => { setShowPlaybackModal(false); openViewer('image'); } : undefined} />}
    </MainLayout>
  );
};

export default ViewEventPage;
